import React, { useState, useContext } from 'react';
import { 
  Box, Heading, Text, Flex, Button, Input, InputGroup, 
  InputRightElement, HStack, VStack, Icon, useColorModeValue,
  useDisclosure, Modal, ModalOverlay, ModalContent, ModalCloseButton, ModalBody, ModalHeader,
  Divider, Link
} from '@chakra-ui/react';
import { MdVideoCall, MdKeyboard, MdLink } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import MeetingManager from '../shared/MeetingManager';
import ActiveMeetings from '../shared/ActiveMeetings';
import AuthContext from '../services/AuthContext';

export default function MeetingsPage() {
  const { user } = useContext(AuthContext);
  const isAdminOrInstructor = user?.role === 'admin' || user?.role === 'instructor';
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [meetingId, setMeetingId] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const navigate = useNavigate();

  const handleJoin = () => {
    if (meetingId.trim() && secretKey.trim()) {
      navigate(`/meet/${meetingId.trim()}`, { state: { secretKey: secretKey.trim() } });
    }
  };

  const bg = useColorModeValue('white', 'gray.900');
  const textColor = useColorModeValue('gray.800', 'white');
  const subTextColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <Box minH="calc(100vh - 80px)" bg={bg} w="100%" pt={{ base: 10, md: 20 }} px={{ base: 4, md: 8, lg: 16 }}>
      <Flex direction={{ base: 'column', lg: 'row' }} justify="space-between" align="center" maxW="1200px" mx="auto" gap={12}>
        
        {/* Left Side Content */}
        <VStack align="flex-start" spacing={8} maxW={{ base: '100%', lg: '50%' }} dir="rtl" w="100%">
          <VStack align="flex-start" spacing={4}>
            <Heading as="h1" size="2xl" color={textColor} fontWeight="normal" lineHeight="1.2">
              فصول مباشرة مميزة.<br/>الآن مجانية للجميع.
            </Heading>
            
            <Text fontSize="xl" color={subTextColor} maxW="500px" lineHeight="1.6">
              أعدنا تصميم خدمتنا للحصص المباشرة لتكون آمنة، بحيث تكون مجانية ومتاحة للجميع.
            </Text>
          </VStack>
          
          <Flex direction={{ base: 'column', sm: 'row' }} gap={4} w="100%" align={{ base: 'stretch', sm: 'center' }}>
            {isAdminOrInstructor && (
              <Button 
                colorScheme="blue" 
                size="lg" 
                rightIcon={<Icon as={MdVideoCall} boxSize={6} mr={2} />} 
                onClick={onOpen}
                fontWeight="medium"
                px={6}
                borderRadius="md"
              >
                حصة جديدة
              </Button>
            )}
            
            <HStack flex={1} maxW="450px" spacing={2}>
              <InputGroup size="lg">
                <InputRightElement pointerEvents="none">
                  <Icon as={MdKeyboard} color="gray.500" boxSize={6} />
                </InputRightElement>
                <Input 
                  placeholder="رقم الحصة (ID)" 
                  value={meetingId}
                  onChange={(e) => setMeetingId(e.target.value)}
                  borderRadius="md"
                  focusBorderColor="blue.500"
                  pl={4}
                  pr={10}
                />
              </InputGroup>
              <InputGroup size="lg">
                <Input 
                  placeholder="الرقم السري" 
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  borderRadius="md"
                  focusBorderColor="blue.500"
                  type="password"
                  onKeyPress={(e) => e.key === 'Enter' && handleJoin()}
                />
              </InputGroup>
              <Button 
                variant="ghost" 
                colorScheme="blue" 
                size="lg" 
                isDisabled={!meetingId.trim() || !secretKey.trim()}
                onClick={handleJoin}
                px={6}
              >
                انضمام
              </Button>
            </HStack>
          </Flex>

          <Divider w="100%" maxW="500px" />

          <Text color={subTextColor} fontSize="md">
            <Link color="blue.500" href="#">مزيد من المعلومات</Link> عن الحصص المباشرة
          </Text>
        </VStack>

        {/* Right Side Image/Carousel area */}
        <VStack flex={1} w="100%" align="center" spacing={8}>
          <Box 
            w="350px" 
            h="350px" 
            rounded="full" 
            bg={useColorModeValue('blue.50', 'blue.900')} 
            display="flex" 
            alignItems="center" 
            justifyContent="center"
            position="relative"
            boxShadow="inner"
          >
            <VStack spacing={6}>
              <Box p={6} bg="blue.500" rounded="full" color="white" boxShadow="xl">
                <Icon as={MdLink} boxSize={12} />
              </Box>
            </VStack>
          </Box>
          <VStack spacing={3} textAlign="center" dir="rtl">
            <Heading size="lg" color={textColor} fontWeight="normal">
              احصل على رابط للمشاركة
            </Heading>
            <Text color={subTextColor} fontSize="md" maxW="350px">
              انقر على <b>حصة جديدة</b> للحصول على رابط يمكنك إرساله إلى الأشخاص الذين تريد الاجتماع بهم
            </Text>
          </VStack>
          {/* Carousel dots mock */}
          <HStack spacing={2} mt={2}>
            <Box w={2} h={2} rounded="full" bg="blue.500" />
            <Box w={2} h={2} rounded="full" bg="gray.300" />
            <Box w={2} h={2} rounded="full" bg="gray.300" />
          </HStack>
        </VStack>
      </Flex>

      {/* Active Meetings Section Below (Only for Admins/Instructors) */}
      {isAdminOrInstructor && (
        <Box maxW="1200px" mx="auto" mt={24} pb={12}>
          <ActiveMeetings />
        </Box>
      )}

      {/* Meeting Manager Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent dir="rtl">
          <ModalHeader>إنشاء حصة جديدة</ModalHeader>
          <ModalCloseButton left={4} right="unset" />
          <ModalBody pb={6}>
            <MeetingManager />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
